import { ResponseCatalog, RequestParams, CertificateModel } from 'Models'
import { FieldNames } from 'Src/constants/FieldNames'
import { ORDER_DIRECTION } from 'Src/constants/Global'
import { ActModel, ActsGridView, ActsUnpaidFitersType, KindActModel } from 'Pages/acts'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FileWithPreview } from 'LibNeo'
import { Callback } from 'Src/types/utilTypes'

type ActState = {
	data: ResponseCatalog<ActModel>
	openedAct: number
	requestParams: RequestParams
	cancel_kinds: { data: ResponseCatalog<KindActModel>; requestParams: RequestParams }
	certificates: CertificateModel[]
	gridView: ActsGridView
	currentFilter: ActsUnpaidFitersType
	counterProtocolGenerationValidation: number
	item: ActModel
	signableActsCount?: number
	chosenActs: number[]
	timer: number | null
}

const actInitialState: ActState = {
	data: new ResponseCatalog<ActModel>(),
	openedAct: null,
	requestParams: new RequestParams(1, 30, null, {
		order_by: FieldNames.STATUS_UPDATED_AT,
		order_direction: ORDER_DIRECTION.DESC,
	}),
	cancel_kinds: { data: new ResponseCatalog<KindActModel>(), requestParams: new RequestParams() },
	certificates: [],
	gridView: ActsGridView.FULL,
	currentFilter: null,
	signableActsCount: undefined,
	counterProtocolGenerationValidation: 0,
	item: null,
	chosenActs: [],
	timer: null,
}

export namespace actNS {
	export type getActs = PayloadAction<{
		params?: RequestParams
		clear?: boolean
		callback?: Callback
	}>
	export type setActs = PayloadAction<ResponseCatalog<ActModel>>
	export type setOpenedAct = PayloadAction<number>
	export type getAct = PayloadAction<number>
	export type clearActs = PayloadAction
	export type setAct = PayloadAction<ActModel>
	export type setActsRequestParams = PayloadAction<RequestParams>
	export type setSignableActsCount = PayloadAction<number>
	export type setActsCancelKinds = PayloadAction<ResponseCatalog<KindActModel>>
	export type setUserSertificates = PayloadAction<Array<CertificateModel>>
	export type setActsGridView = PayloadAction<ActsGridView>
	export type setActsCurrentFilter = PayloadAction<ActsUnpaidFitersType>
	export type counterProtocolGenerationValidation = PayloadAction<number>
	export type uploadActsStatuses = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type uploadActs = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type cancelPaids = PayloadAction
	export type partnerSignActs = PayloadAction<{
		ids: number[]
		code: string
		callback?: Callback
		errorCallback?: Callback
	}>
	export type DSSignActs = PayloadAction<{ ids: number[]; certNumber: string; callback?: Callback }>
	export type createActSmsCode = PayloadAction<{
		ids: number[]
		callback?: Callback
		errorCallback?: Callback
	}>
	export type getActsCancelKinds = PayloadAction<RequestParams>
	export type cancelActs = PayloadAction<{
		act_ids: number[]
		kind: string
		message?: string
		callback?: Callback
	}>
	export type restoreActs = PayloadAction<{ ids: number[]; callback?: Callback }>
	export type deleteActs = PayloadAction<{ ids: number[]; callback?: Callback }>
	export type deleteMassActs = PayloadAction<{
		created_at_from: string
		created_at_to: string
		callback?: Callback
	}>
	export type onPartnerSignMassActs = PayloadAction<{
		created_at_from: string
		created_at_to: string
		callback?: Callback
	}>
	export type getUserSerticates = PayloadAction
	export type clearUnusedSms = PayloadAction<number>
	export type unblockAct = PayloadAction<number>
	export type generateProtocol = PayloadAction<{ act_ids: number[]; callback?: Callback }>
	export type archiveActs = PayloadAction<RequestParams>
	export type getSignableActs = PayloadAction
	export type refundActs = PayloadAction<{ act_ids: number[]; callback?: Callback }>
	export type getActsFile = PayloadAction<{ file: string; filename: string }>
	export type partnerCanSign = PayloadAction<{ act_ids: number[]; callback?: Callback }>
	export type sendRefundEmails = PayloadAction<{ act_ids: number[]; callback?: Callback }>
	export type uploadPaymentOrders = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type uploadThirdPartyActsAndInvoices = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type generateProtocolOnOpen = PayloadAction<number>
	export type remarkActs = PayloadAction<{
		act_ids: number[]
		message: string
		callback?: Callback
	}>
	export type uploadReestr = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type uploadPromotionReestr = PayloadAction<FileWithPreview | FileWithPreview[]>
	export type annulActs = PayloadAction<{ act_ids: number[]; callback?: Callback }>
	export type setItem = PayloadAction<ActModel>
	export type setChosenActs = PayloadAction<number[]>
	export type setTimer = PayloadAction<number>
}

export const actSlice = createSlice({
	name: 'act',
	initialState: actInitialState,
	reducers: {
		getActs: (state, action: actNS.getActs) => {},
		setActs: (state, action: actNS.setActs) => {
			state.data = action.payload
		},
		setOpenedAct: (state, action: actNS.setOpenedAct) => {
			state.openedAct = action.payload
		},
		getAct: (state, action: actNS.getAct) => {},
		clearActs: (state, action: actNS.clearActs) => {
			state.data = new ResponseCatalog<ActModel>()
			state.requestParams = Object.assign(new RequestParams(), {
				order_by: state.requestParams.order_by,
				order_direction: state.requestParams.order_direction,
			})
		},
		setAct: (state, action: actNS.setAct) => {
			const { id } = action.payload
			state.data.entries = state.data.entries.map((item: ActModel): ActModel => {
				if (item.id === id) {
					return action.payload
				}
				return item
			})
			state.openedAct = id
		},
		setItem: (state, action: actNS.setItem) => {
			state.item = action.payload
		},
		setActsRequestParams: (state, action: actNS.setActsRequestParams) => {
			state.requestParams = action.payload
		},
		setSignableActsCount: (state, action: actNS.setSignableActsCount) => {
			state.signableActsCount = action.payload
		},
		setActsCancelKinds: (state, action: actNS.setActsCancelKinds) => {
			state.cancel_kinds.data = action.payload
		},
		setUserSertificates: (state, action: actNS.setUserSertificates) => {
			state.certificates = action.payload
		},
		setActsGridView: (state, action: actNS.setActsGridView) => {
			state.gridView = action.payload
		},
		setActsCurrentFilter: (state, action: actNS.setActsCurrentFilter) => {
			state.currentFilter = action.payload
		},
		counterProtocolGenerationValidation: (
			state,
			action: actNS.counterProtocolGenerationValidation
		) => {
			state.counterProtocolGenerationValidation = action.payload
		},
		uploadActsStatuses: (state, action: actNS.uploadActsStatuses) => {},
		uploadActs: (state, action: actNS.uploadActs) => {},
		cancelPaids: (state, action: actNS.cancelPaids) => {},
		partnerSignActs: (state, action: actNS.partnerSignActs) => {},
		DSSignActs: (state, action: actNS.DSSignActs) => {},
		createActSmsCode: (state, action: actNS.createActSmsCode) => {},
		getActsCancelKinds: (state, action: actNS.getActsCancelKinds) => {},
		cancelActs: (state, action: actNS.cancelActs) => {},
		restoreActs: (state, action: actNS.restoreActs) => {},
		deleteActs: (state, action: actNS.deleteActs) => {},
		deleteMassActs: (state, action: actNS.deleteMassActs) => {},
		onPartnerSignMassActs: (state, action: actNS.onPartnerSignMassActs) => {},
		getUserSerticates: (state, action: actNS.getUserSerticates) => {},
		clearUnusedSms: (state, action: actNS.clearUnusedSms) => {},
		unblockAct: (state, action: actNS.unblockAct) => {},
		generateProtocol: (state, action: actNS.generateProtocol) => {},
		archiveActs: (state, action: actNS.archiveActs) => {},
		getSignableActs: (state, action: actNS.getSignableActs) => {},
		refundActs: (state, action: actNS.refundActs) => {},
		getActsFile: (state, action: actNS.getActsFile) => {},
		partnerCanSign: (state, action: actNS.partnerCanSign) => {},
		sendRefundEmails: (state, action: actNS.sendRefundEmails) => {},
		uploadPaymentOrders: (state, action: actNS.uploadPaymentOrders) => {},
		uploadThirdPartyActsAndInvoices: (state, action: actNS.uploadThirdPartyActsAndInvoices) => {},
		generateProtocolOnOpen: (state, action: actNS.generateProtocolOnOpen) => {},
		remarkActs: (state, action: actNS.remarkActs) => {},
		uploadReestr: (state, action: actNS.uploadReestr) => {},
		uploadPromotionReestr: (state, action: actNS.uploadPromotionReestr) => {},
		annulActs: (state, action: actNS.annulActs) => {},
		setChosenActs: (state, action: actNS.setChosenActs) => {
			state.chosenActs = [...new Set<number>(action.payload)]
		},
		clearChosenActs: (state) => {
			state.chosenActs = []
		},
		setTimer: (state, action: actNS.setTimer) => {
			state.timer = action.payload
		},
	},
})
